import { FC, useEffect, useState, useMemo } from "react";
import IconButton from "components/atoms/button/IconButton";
import Form from "components/atoms/form/Form";
import Icon from "components/atoms/icon/Icon";
import Check3Icon from "components/atoms/icon/icon-type/Check3Icon";
import CheckIcon from "components/atoms/icon/icon-type/CheckIcon";
import LoadingScreen from "components/atoms/loading-screen/LoadingScreen";
import Table from "components/molecules/table/Table";
import { useMultiStepFormContext } from "lib/context/MultiStepFormContext/MultiStepFormContext";
import { IconType } from "lib/helpers/constants/iconTypes";
import { useForm } from "react-hook-form";
import { Column } from "react-table";
import { Fragment } from "react/jsx-runtime";
import { useUploadCustomersStoreFile } from "lib/hooks/mutations/Customers/useUploadCustomersStoreFile";
import { useTranslation } from "react-i18next"

export interface IProps {
  setStep: (value: number) => void;
  dataCols: any;
  onUpdateData: (updatedData: any[]) => void;
  themeColors: {
    primary: string;
  };
}

const StoreFields: FC<IProps> = ({
  setStep,
  dataCols,
  onUpdateData,
  themeColors,
}) => {
  const { handleSubmit } = useForm();
  const { formData } = useMultiStepFormContext<any>();
  const {t} = useTranslation();

  const { mutateAsync: storeUploadedAppointment, isLoading } =
    useUploadCustomersStoreFile();

  const [tableData, setTableData] = useState(formData.data || []);

  useEffect(() => {
    setTableData(formData.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemove = (index: number) => {
    const newData = tableData.filter((_: any, i: number) => i !== index);
    setTableData(newData);
    onUpdateData(newData);
  };

  const actionsColumn = {
    Header: `${t("Delete")}`,
    accessor: "delete",
    Cell: ({ row }: any) => (
      <button
        type="button"
        onClick={() => handleRemove(row.index)}
        className="text-red-600 hover:text-red-800"
      >
        <Icon iconType={IconType.DELETE_ICON} color="#7D8592" />
      </button>
    ),
  };

  const columns = useMemo<Column<any>[]>(
    () => [actionsColumn, ...dataCols.filter((col: any) => col.isChecked)],
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataCols, tableData]
  ) as Column<object>[];

  const onSubmit = () => {
    const updatedData = {
      costumers: tableData,
      file_name: formData?.file,
    };

    storeUploadedAppointment(updatedData)
      .then(() => {
        setStep(4);
      })
      .catch(() => {});
  };

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      className="mb-0 flex flex-col pl-4 pb-2"
    >
      <div className="border-l-[1.5px] border-dashed border-[#D8D8D8] pl-7 pb-9 relative bg-transparent">
        <div className="pl-2 text-[14px] font-inter-medium pb-5">
          {t("Upload CSV")}
        </div>
        <div className="absolute top-[0] left-[-12px] z-9999">
          <CheckIcon color={themeColors.primary} />
        </div>
      </div>
      <div className="border-l-[1.5px] border-dashed border-[#D8D8D8] pl-7 pb-9 relative bg-transparent">
        <div className="pl-2 text-[14px] font-inter-medium pb-5 ">
          {t("Only adjust the fields you want to import")}
        </div>
        <div className="absolute top-[0] left-[-12px] z-9999 ">
          <CheckIcon color={themeColors.primary} />
        </div>
      </div>
      <div className="pl-7 relative bg-transparent">
        <div className="pl-2 text-[14px] font-inter-medium pb-5 ">
          {t("Remove the customers here that you don't want to import")}
        </div>
        <div
          className="rounded-[20px] p-4 w-full "
          style={{
            backgroundColor: `${themeColors.primary}0D`,
          }}
        >
          <div className="overflow-auto horizontalOverflowScrollStyle w-full h-full px-5 ">
            <Fragment>
              <Table
                style={{
                  backgroundColor: `${themeColors.primary}0D`,
                }}
                classes={{ subContainer: "!p-0" }}
                columns={columns}
                data={tableData || []}
                search={""}
                isColumnSorted={true}
              />
            </Fragment>
          </div>
        </div>
        <div className="absolute top-[0] left-[-12px] z-9999 ">
          <Check3Icon color={themeColors.primary} />
        </div>
      </div>
      <div className="flex gap-5 justify-end pt-5">
        <IconButton
          icon={<Icon iconType={IconType.UPLOAD_FILE_ICON} />}
          className="w-full max-w-[150px]"
          disabled={isLoading}
        >
          {isLoading ? t("Creating...") : t("Continue")}
        </IconButton>
      </div>
      {isLoading && <LoadingScreen />}
    </Form>
  );
};

export default StoreFields;
